import {
  useEffect, useState, useRef, useCallback
} from "react";
import { LensSDKException, type UserBusinessRole } from "lens-platform-sdk";
import { useLensPlatformClient } from "./useLensPlatformClient";

/**
 * Hook for creating invitations for a business.
 */
export const useCreateBusinessInvitation = (businessId?: string) => {
  const lensPlatformClient = useLensPlatformClient();
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<Awaited<ReturnType<typeof lensPlatformClient.business.createInvitation>>>();

  // Keep up with mounted/unmounted state
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const createBusinessInvitation = useCallback(async (
    email: string,
    subscriptionId?: string,
    role: UserBusinessRole = "Member"
  ) => {
    let lensSDKException: LensSDKException | undefined;

    if (businessId) {
      try {
        if (mounted.current) {
          setLoading(true);
        }
        const response = await lensPlatformClient.business.createInvitation(businessId, email, subscriptionId, role);

        if (mounted.current) {
          setResponse(response);
        }
      } catch (exception) {
        if (exception instanceof LensSDKException) {
          lensSDKException = exception;
        }
      } finally {
        if (mounted.current) {
          setLoading(false);
        }
      }
    }

    return lensSDKException;
  }, [lensPlatformClient.business, businessId]);

  return {
    createBusinessInvitation,
    response,
    loading
  };
};
